import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Col, Form, Row, Table, OverlayTrigger, Tooltip, Accordion } from 'react-bootstrap';
import {
    FaCheck,
    FaEye,
    FaSyncAlt,
    FaTimes,
    FaShoppingBag,
    FaMapMarkerAlt,
    FaFileInvoice,
    FaUser,
    FaPhone,
    FaCalendar,
    FaSearch
} from 'react-icons/fa';
import { LinkContainer } from 'react-router-bootstrap';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import '../../assets/styles/orders.screen.css';
import Loader from '../../components/layout/Loader';
import Message from '../../components/layout/Message';
import Meta from '../../components/layout/Meta';
import Paginate from '../../components/ui/Paginate';
import { useGetUserOrdersQuery, useRepeatOrderMutation } from '../../slices/orderApiSlice';
import useIsMobile from '../../utils/useIsMobile';

const ORDER_STATUSES = [
    { value: '', label: 'Todos los Estados' },
    { value: 'CREATED', label: 'Creado' },
    { value: 'CANCELLED', label: 'Cancelado' },
    { value: 'INVOICED', label: 'Facturado' },
    { value: 'PAID', label: 'Pagado' },
    { value: 'DELIVERED', label: 'Enviado' },
];

// Compact Shipping Address Component
const placeLabel = (address) => [address?.city, address?.state].filter(Boolean).join(', ');

const streetLabel = (address) =>
    [address?.address1, address?.number]
        .filter((part) => part && part !== 'NA' && part !== 'SN')
        .join(' ')
        .trim();

const pickupTitle = (address) => {
    if (address?.pickupLocationId) {
        const name = (address.reference || '').trim();
        if (name && name !== 'Punto de retiro') return name;
        return address.name || 'Punto de retiro';
    }
    return 'Recoger en el local';
};

const CompactShippingAddress = ({ address }) => {
    if (!address) return <span className="text-muted">N/A</span>;

    const isPickup = address.label === 'pickup';
    const place = placeLabel(address);
    const street = streetLabel(address);
    const primary = isPickup ? pickupTitle(address) : (street || 'Dirección');
    const fullAddress = [isPickup ? pickupTitle(address) : street, place].filter(Boolean).join(', ');

    const tooltipContent = (
        <div className="text-start">
            <div><strong>{isPickup ? 'Retiro en:' : 'Envío a:'}</strong></div>
            <div className="mt-2"><FaUser className="me-2" />{address.name}</div>
            {fullAddress && (
                <div className="mt-1"><FaMapMarkerAlt className="me-2" />{fullAddress}</div>
            )}
            <div className="mt-1"><FaPhone className="me-2" />{address.phoneNumber}</div>
        </div>
    );

    return (
        <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`shipping-tooltip-${address._id || primary}`}>{tooltipContent}</Tooltip>}
        >
            <div className="order-ship-cell">
                <FaMapMarkerAlt className={`order-ship-icon ${isPickup ? 'text-info' : 'text-success'}`} />
                <span className="order-ship-copy">
                    <span className="order-ship-primary">{primary}</span>
                    {place && <span className="order-ship-place">{place}</span>}
                </span>
            </div>
        </OverlayTrigger>
    );
};

// Compact Billing Address Component
const CompactBillingAddress = ({ billingAddress }) => {
    if (!billingAddress) return <span className="text-muted">N/A</span>;

    const docId = billingAddress.taxDocId || billingAddress.docId || '';
    const typeLabel = billingAddress.type === 'J' ? 'Jurídica' :
                     billingAddress.type === 'NF' ? 'Extranjero' : 'Natural';

    const tooltipContent = (
        <div className="text-start">
            <div><strong>Datos de Facturación:</strong></div>
            <div className="mt-2"><FaUser className="me-2" />{billingAddress.name}</div>
            <div className="mt-1"><FaFileInvoice className="me-2" />{docId} ({typeLabel})</div>
            <div className="mt-1"><FaPhone className="me-2" />{billingAddress.phoneNumber}</div>
        </div>
    );

    return (
        <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`billing-tooltip`}>{tooltipContent}</Tooltip>}
        >
            <div className="d-flex align-items-center justify-content-center gap-2">
                <FaFileInvoice className="text-brand" />
                <span className="text-truncate" style={{ maxWidth: '150px' }}>
                    {billingAddress.name}
                </span>
            </div>
        </OverlayTrigger>
    );
};

const OrdersScreen = () => {
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const { pageNumber } = useParams();

    // Search and filter state
    const [searchTerm, setSearchTerm] = useState('');
    const [dateFilter, setDateFilter] = useState(null); // Date object for DatePicker
    const [statusFilter, setStatusFilter] = useState('');
    const [paymentFilter, setPaymentFilter] = useState('');

    const activeFiltersCount = [searchTerm, dateFilter, statusFilter, paymentFilter].filter(Boolean).length;
    const [filtersAccordionKey, setFiltersAccordionKey] = useState(isMobile ? null : '0');

    useEffect(() => {
        if (!isMobile && filtersAccordionKey === null) {
            setFiltersAccordionKey('0');
        }

        if (isMobile && activeFiltersCount > 0 && filtersAccordionKey === null) {
            setFiltersAccordionKey('0');
        }
    }, [isMobile, activeFiltersCount, filtersAccordionKey]);

    // Fetch orders with backend pagination
    const {
        data: ordersData,
        isLoading: loadingOrders,
        error: errorLoadingOrders,
    } = useGetUserOrdersQuery({
        pageNumber: pageNumber || 1,
        search: searchTerm,
        date: dateFilter ? dateFilter.toISOString().split('T')[0] : '', // Convert Date to YYYY-MM-DD
        status: statusFilter,
        paymentStatus: paymentFilter,
    });

    const [repeatOrder, { isLoading: loadingRepeatOrder }] = useRepeatOrderMutation();

    // Extract orders array and pagination from backend response
    const orders = Array.isArray(ordersData?.orders) ? ordersData.orders : [];
    const pagination = ordersData?.pagination || { page: 1, pages: 1, total: 0 };

    const handleClearFilters = () => {
        setSearchTerm('');
        setDateFilter(null); // Reset to null for DatePicker
        setStatusFilter('');
        setPaymentFilter('');
        navigate('/orders');
    };

    const repeatOrderHandler = async (id) => {
        try {
            await repeatOrder(id).unwrap();
            navigate('/cart');
        } catch (err) {
            toast.error(err?.data?.message || err.error);
        }
    };


    const renderStatusBadge = (order) => {
        if (order.isPaid) {
            if (order.isDelivered) {
                return (
                    <div className="status-badge delivered">
                        <FaCheck /> Enviado ({order.deliveredAt.substring(0, 10)})
                    </div>
                );
            }
            return (
                <div className="status-badge paid">
                    <FaCheck /> Pagado ({order.paidAt.substring(0, 10)})
                </div>
            );
        }
        return (
            <div className="status-badge pending">
                <FaTimes /> Pendiente
            </div>
        );
    };

    const renderActionButtons = (order) => (
        <div className="action-buttons justify-content-center">
            <LinkContainer to={`/order/${order._id}`}>
                <Button className="btn-sm" variant="outline-primary" title="Ver Detalles">
                    <FaEye />
                </Button>
            </LinkContainer>
            {(order.orderStatus === 'CREATED' || order.orderStatus === 'CANCELLED') && (
                <Button
                    className="btn-sm"
                    variant="outline-secondary"
                    onClick={() => repeatOrderHandler(order._id)}
                    disabled={loadingRepeatOrder}
                    title="Editar pedido"
                >
                    <FaSyncAlt />
                </Button>
            )}
            {order.orderStatus === 'INVOICED' && (
                <Button
                    className="btn-sm"
                    variant="outline-secondary"
                    onClick={() => repeatOrderHandler(order._id)}
                    disabled={loadingRepeatOrder}
                    title="Repetir pedido"
                >
                    <FaSyncAlt />
                </Button>
            )}
        </div>
    );

    return (
        <div className="orders-screen" id="orders-section">
            <Meta title="Mis Pedidos" />

            <Row className="mb-4">
                <Col>
                    <h1 className="d-flex align-items-center mb-3">
                        <FaShoppingBag className="me-2" /> Mis Pedidos
                    </h1>
                </Col>
            </Row>

            {/* Filters Section */}
            <Accordion
                className="filters-accordion mb-4"
                activeKey={filtersAccordionKey}
                onSelect={(eventKey) => setFiltersAccordionKey(eventKey)}
            >
                <Accordion.Item eventKey="0" className="filters-card">
                    <Accordion.Header>
                        <div className="d-flex align-items-center justify-content-between w-100 pe-2">
                            <div className="d-flex align-items-center gap-2">
                                <FaSearch size={13} />
                                <span>Filtros de pedidos</span>
                            </div>
                            <span className="filters-pill">
                                {activeFiltersCount > 0 ? `${activeFiltersCount} activos` : 'Opcionales'}
                            </span>
                        </div>
                    </Accordion.Header>
                    <Accordion.Body>
                        <Row className="gy-3">
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label column={false} className="small fw-bold mb-2 d-flex align-items-center gap-1">
                                        <FaSearch size={12} className="text-brand-light" />
                                        Buscar por ID
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="ID del pedido..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label column={false} className="small fw-bold mb-2 d-flex align-items-center gap-1">
                                        <FaCalendar size={12} className="text-brand-light" />
                                        Buscar por Fecha
                                    </Form.Label>
                                    <DatePicker
                                        selected={dateFilter}
                                        onChange={(date) => setDateFilter(date)}
                                        dateFormat="dd/MM/yyyy"
                                        className="form-control"
                                        placeholderText="Seleccionar fecha..."
                                        isClearable
                                        maxDate={new Date()}
                                        todayButton="Hoy"
                                        showPopperArrow={false}
                                        calendarClassName="custom-calendar"
                                        showMonthYearDropdown={false}
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label column={false} className="small fw-bold mb-2">Estado del Pedido</Form.Label>
                                    <Form.Select
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                    >
                                        {ORDER_STATUSES.map((status) => (
                                            <option key={status.value} value={status.value}>
                                                {status.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label column={false} className="small fw-bold mb-2">Estado de Pago</Form.Label>
                                    <Form.Select
                                        value={paymentFilter}
                                        onChange={(e) => setPaymentFilter(e.target.value)}
                                    >
                                        <option value="">Todos los Estados</option>
                                        <option value="paid">Pagado</option>
                                        <option value="pending">Pendiente</option>
                                        <option value="delivered">Entregado</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col xs={12}>
                                <div className="d-flex gap-2 flex-wrap">
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={handleClearFilters}
                                        className="d-flex align-items-center gap-1"
                                    >
                                        <FaTimes size={12} /> Limpiar Filtros
                                    </Button>
                                    <span className="text-muted small align-self-center">
                                        Mostrando {orders.length} de {pagination.total} pedidos
                                    </span>
                                </div>
                            </Col>
                        </Row>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>

            {/* Orders List */}
            {loadingOrders ? (
                <Loader />
            ) : errorLoadingOrders ? (
                <Message variant="danger">
                    {errorLoadingOrders?.data?.message || errorLoadingOrders.error}
                </Message>
            ) : orders.length === 0 ? (
                <Message variant="info">No se encontraron pedidos con los filtros aplicados</Message>
            ) : (
                <>
                    {/* Desktop View */}
                    {!isMobile && (
                        <div className="admin-table-container shadow-sm mb-4">
                            <Table hover responsive className="admin-table m-0">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>FECHA</th>
                                        <th>TOTAL</th>
                                        <th className="text-center">ENVÍO</th>
                                        <th className="text-center">FACTURACIÓN</th>
                                        <th>ESTADO</th>
                                        <th className="text-center">ACCIONES</th>
                                    </tr>
                                </thead>
                            <tbody>
                                {orders.map((order) => (
                                    <tr key={order._id}>
                                        <td className="align-middle text-truncate">
                                            {order._id.substring(order._id.length - 8)}
                                        </td>
                                        <td className="align-middle">
                                            {order.createdAt.substring(0, 10)}
                                        </td>
                                        <td className="align-middle fw-bold">${order.totalPrice}</td>
                                        <td className="align-middle text-center">
                                            <CompactShippingAddress address={order.shippingAddress} />
                                        </td>
                                        <td className="align-middle text-center">
                                            <CompactBillingAddress billingAddress={order.billingAddress} />
                                        </td>
                                        <td className="align-middle">{renderStatusBadge(order)}</td>
                                        <td className="align-middle text-center">{renderActionButtons(order)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            </Table>
                        </div>
                    )}

                    {/* Mobile View */}
                    {isMobile && (
                        <div className="d-md-none">
                            {orders.map((order) => (
                                <Card key={order._id} className="order-mobile-card-modern mb-4 border-0">
                                    {/* Order Header with gradient background */}
                                    <div className="order-mobile-header p-3">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <div className="d-flex align-items-center gap-2">
                                                <FaShoppingBag className="text-white" size={18} />
                                                <span className="text-white fw-bold">
                                                    Pedido #{order._id.substring(order._id.length - 8)}
                                                </span>
                                            </div>
                                            {renderStatusBadge(order)}
                                        </div>
                                        <div className="text-white-50 small">
                                            {new Date(order.createdAt).toLocaleDateString('es-EC', {
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric'
                                            })}
                                        </div>
                                    </div>

                                    <Card.Body className="p-3">
                                        {/* Price Section */}
                                        <div className="order-price-section mb-3 p-3 bg-light rounded">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <span className="text-muted">Total del Pedido</span>
                                                <span className="fs-4 fw-bold text-brand">${order.totalPrice}</span>
                                            </div>
                                        </div>

                                        {/* Shipping Address Section */}
                                        <div className="order-info-section mb-3">
                                            <div className="d-flex align-items-center gap-2 mb-2">
                                                <div className={`icon-badge ${order.shippingAddress?.label === 'pickup' ? 'bg-info-subtle' : 'bg-success-subtle'}`}>
                                                    <FaMapMarkerAlt className={order.shippingAddress?.label === 'pickup' ? 'text-info' : 'text-success'} />
                                                </div>
                                                <span className="fw-semibold text-dark">
                                                    {order.shippingAddress?.label === 'pickup' ? 'Recoger en local' : 'Dirección de envío'}
                                                </span>
                                            </div>
                                            <div className="ms-4 ps-2">
                                                <div className="text-dark mb-1">
                                                    <FaUser className="me-2 text-muted" size={12} />
                                                    {order.shippingAddress?.name}
                                                </div>
                                                {order.shippingAddress?.label === 'pickup' &&
                                                    pickupTitle(order.shippingAddress) !== order.shippingAddress?.name && (
                                                    <div className="text-muted small text-break">
                                                        {pickupTitle(order.shippingAddress)}
                                                    </div>
                                                )}
                                                {streetLabel(order.shippingAddress) && (
                                                    <div className="text-muted small text-break">
                                                        {streetLabel(order.shippingAddress)}
                                                    </div>
                                                )}
                                                {placeLabel(order.shippingAddress) && (
                                                    <div className="order-ship-place text-break">
                                                        {placeLabel(order.shippingAddress)}
                                                    </div>
                                                )}
                                                <div className="text-muted small mt-1">
                                                    <FaPhone className="me-2" size={11} />
                                                    {order.shippingAddress?.phoneNumber}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Billing Address Section */}
                                        <div className="order-info-section mb-3">
                                            <div className="d-flex align-items-center gap-2 mb-2">
                                                <div className="icon-badge bg-brand-subtle">
                                                    <FaFileInvoice className="text-brand" />
                                                </div>
                                                <span className="fw-semibold text-dark">Datos de Facturación</span>
                                            </div>
                                            <div className="ms-4 ps-2">
                                                <div className="text-dark mb-1">
                                                    <FaUser className="me-2 text-muted" size={12} />
                                                    {order.billingAddress?.name}
                                                </div>
                                                <div className="text-muted small mb-1">
                                                    <FaFileInvoice className="me-2" size={11} />
                                                    {order.billingAddress?.taxDocId || order.billingAddress?.docId}
                                                    {' '}({order.billingAddress?.type === 'J' ? 'Jurídica' : order.billingAddress?.type === 'NF' ? 'Extranjero' : 'Natural'})
                                                </div>
                                                <div className="text-muted small">
                                                    <FaPhone className="me-2" size={11} />
                                                    {order.billingAddress?.phoneNumber}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="order-actions-mobile mt-3 pt-3 border-top">
                                            <LinkContainer to={`/order/${order._id}`}>
                                                <Button variant="primary" className="w-100 mb-2 d-flex align-items-center justify-content-center gap-2">
                                                    <FaEye /> Ver Detalles del Pedido
                                                </Button>
                                            </LinkContainer>
                                            {(order.orderStatus === 'CREATED' || order.orderStatus === 'CANCELLED' || order.orderStatus === 'INVOICED') && (
                                                <Button
                                                    variant="outline-secondary"
                                                    className="w-100 d-flex align-items-center justify-content-center gap-2"
                                                    onClick={() => repeatOrderHandler(order._id)}
                                                    disabled={loadingRepeatOrder}
                                                >
                                                    <FaSyncAlt />
                                                    {order.orderStatus === 'CREATED' || order.orderStatus === 'CANCELLED' ? 'Editar Pedido' : 'Repetir Pedido'}
                                                </Button>
                                            )}
                                        </div>
                                    </Card.Body>
                                </Card>
                            ))}
                        </div>
                    )}

                    {/* Pagination */}
                    <Paginate
                        pages={pagination.pages}
                        page={pagination.page}
                        isUserOrders={true}
                        scrollTargetId="orders-section"
                    />
                </>
            )}
        </div>
    );
};

export default OrdersScreen;

